<script setup lang="ts">
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { hexToRgb } from '@layouts/utils'
import { userAbilityRulesStorage } from '@layouts/plugins/casl'

const { global } = useTheme()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()
const oauth2Enable = ref(useStorage('oauth2Enable').value)
let serverStatus = 'on'

interface MaintenanceStatus {
  maintenancePending: boolean
  id?: string | number
  message?: string
  deadline?: string | null
  updatedAt?: string | number
}

const checkServerStatus = () => {
  fetch('/sys/health')
    .then(response => {
      if (response.ok) {
        if (serverStatus == 'off') {
          stopServerStatusCheck()
          serverStatus = 'on'
          useStorage('userData', {}).value = null
          useCookie('userToken').value = null
          // 使用共享的 ref 清空，確保與 isHidden 函數同步
          userAbilityRulesStorage.value = []
          console.error('返回首頁')
          window.location.href = '/login'
        }
        console.debug('Server is up and running.')
      }
      else {
        console.error('Server is down.')
        if (serverStatus == 'on') {
          serverStatus = 'off'
          $swal({
            position: 'center',
            icon: 'warning',
            title: '伺服器維護更新中, 請稍後...',
            showConfirmButton: false,
            allowOutsideClick: false,
          })
        }
      }
    })
    .catch(error => {
      console.error('Cannot reach server:', error)
    })
}

let serverStatusInterval: ReturnType<typeof setInterval> | null = null

const startServerStatusCheck = () => {
  if (!serverStatusInterval) {
    serverStatusInterval = setInterval(checkServerStatus, 10000)
  }
}

const stopServerStatusCheck = () => {
  if (serverStatusInterval) {
    clearInterval(serverStatusInterval)
    serverStatusInterval = null
  }
}

// // 開始檢查伺服器狀態
// startServerStatusCheck()

let eventSource: EventSource | null = null
let sseReconnectTimer: ReturnType<typeof setTimeout> | null = null
let sseWatchdogInterval: ReturnType<typeof setInterval> | null = null
let maintenancePollInterval: ReturnType<typeof setInterval> | null = null
let lastSseAt = Date.now()
let lastShownMaintenanceId = ''
let lastFallbackMessage = ''

const markSseAlive = () => {
  lastSseAt = Date.now()
}

const parseMaintenanceStatus = (payload: unknown): MaintenanceStatus | null => {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload) as MaintenanceStatus
    }
    catch {
      return {
        maintenancePending: true,
        message: payload,
        updatedAt: payload,
      }
    }
  }

  if (payload && typeof payload === 'object')
    return payload as MaintenanceStatus

  return null
}

const getMaintenanceNoticeId = (status: MaintenanceStatus) => {
  return `${status.id ?? ''}:${status.updatedAt ?? status.message ?? ''}`
}

const showMaintenanceNotice = (status: MaintenanceStatus | null) => {
  if (!status?.maintenancePending || !status.message)
    return

  const noticeId = getMaintenanceNoticeId(status)
  if (noticeId && noticeId === lastShownMaintenanceId)
    return

  lastShownMaintenanceId = noticeId

  $swal({
    position: 'center',
    icon: 'warning',
    title: status.message,
    text: status.deadline ? `維護時間：${status.deadline}` : undefined,
    confirmButtonText: '我知道了',
    showConfirmButton: true,
    showCloseButton: true,
    allowOutsideClick: false,
  })
}

const showFallbackSseMessage = (message: string) => {
  if (!message || message === lastFallbackMessage)
    return

  lastFallbackMessage = message
  $swal({
    position: 'top',
    title: message,
    showConfirmButton: false,
    showCloseButton: true,
    allowOutsideClick: false,
  })
}

const scheduleSseReconnect = () => {
  if (sseReconnectTimer)
    return

  sseReconnectTimer = setTimeout(() => {
    sseReconnectTimer = null
    connectSse()
  }, 3000)
}

const connectSse = () => {
  if (sseReconnectTimer) {
    clearTimeout(sseReconnectTimer)
    sseReconnectTimer = null
  }

  eventSource?.close()
  markSseAlive()

  eventSource = new EventSource('/sys/sse')

  eventSource.onopen = () => {
    markSseAlive()
  }

  eventSource.onmessage = event => {
    markSseAlive()
    showFallbackSseMessage(event.data)
  }

  eventSource.addEventListener('ping', () => {
    markSseAlive()
  })

  eventSource.addEventListener('maintenance', event => {
    markSseAlive()
    showMaintenanceNotice(parseMaintenanceStatus((event as MessageEvent).data))
  })

  eventSource.onerror = error => {
    console.error('SSE connection error', error)
    startServerStatusCheck()
    eventSource?.close()
    scheduleSseReconnect()
  }
}

const startSseWatchdog = () => {
  if (sseWatchdogInterval)
    return

  sseWatchdogInterval = setInterval(() => {
    if (Date.now() - lastSseAt > 30000)
      connectSse()
  }, 10000)
}

const checkMaintenanceStatus = async () => {
  try {
    const response = await fetch(`/sys/maintenance/status?t=${Date.now()}`, {
      headers: { Accept: 'application/json' },
    })

    if (!response.ok)
      throw new Error(`HTTP ${response.status}`)

    const status = await response.json() as MaintenanceStatus
    showMaintenanceNotice(parseMaintenanceStatus(status))
  }
  catch (error) {
    console.error('Cannot fetch maintenance status:', error)
    startServerStatusCheck()
  }
}

const startMaintenancePolling = () => {
  if (maintenancePollInterval)
    return

  checkMaintenanceStatus()
  maintenancePollInterval = setInterval(checkMaintenanceStatus, 10000)
}

onMounted(() => {
  connectSse()
  startSseWatchdog()
  startMaintenancePolling()
})

onBeforeUnmount(() => {
  eventSource?.close()

  if (sseReconnectTimer)
    clearTimeout(sseReconnectTimer)
  if (sseWatchdogInterval)
    clearInterval(sseWatchdogInterval)
  if (maintenancePollInterval)
    clearInterval(maintenancePollInterval)
  stopServerStatusCheck()
})
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />

      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>

<style>
.custom-swal .swal2-title {
  font-size: 24px;  /* 標題字體大小 */
}

.custom-swal .swal2-content {
  font-size: 16px;  /* 內容字體大小 */
}
</style>
